/**
 * One review thread = a top-level comment + its replies + an inline reply composer.
 * Used inside ReviewModal's right rail.
 */
import { useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Reply, CheckCircle2, RotateCcw, Send, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import {
  createReview, patchReview, deleteReview,
  type AssetReview,
} from '@/lib/queries';
import MentionTextarea, { MentionBody } from '@/components/creative/MentionTextarea';
const VoiceClip = ({ url }: { url?: string | null }) => url ? <audio controls src={url} className="h-8 mt-1 max-w-full" /> : null;
import { cn } from '@/lib/cn';

interface Props {
  parent: AssetReview;
  replies: AssetReview[];
  active: boolean;
  isVideo: boolean;
  currentUserId: string;
  isStaff: boolean;
  onActivate: () => void;
  onSeek?: (sec: number) => void;
  fmtTime: (sec: number) => string;
  relTime: (iso: string) => string;
}

export default function ReviewThread({
  parent, replies, active, isVideo, currentUserId, isStaff,
  onActivate, onSeek, fmtTime, relTime,
}: Props) {
  const qc = useQueryClient();
  const [composerOpen, setComposerOpen] = useState(false);
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);

  const isResolved = parent.status === 'resolved';
  const canResolve = isStaff || parent.author_id === currentUserId;
  const hasDrawing = (parent.drawing?.length ?? 0) > 0;

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['asset-reviews', parent.asset_id] });
    qc.invalidateQueries({ queryKey: ['creative-reviews', parent.creative_id] });
    qc.invalidateQueries({ queryKey: ['unread-count'] });
  }

  const replyMut = useMutation({
    mutationFn: () => createReview(parent.asset_id, {
      body: body.trim(),
      parent_id: parent.id,
      mentioned_user_ids: mentions,
    }),
    onSuccess: () => {
      toast.success('Reply posted');
      setBody('');
      setMentions([]);
      setComposerOpen(false);
      invalidate();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Reply failed'),
  });

  const toggleResolve = useMutation({
    mutationFn: () => patchReview(parent.id, { status: isResolved ? 'open' : 'resolved' }),
    onSuccess: () => {
      toast.success(isResolved ? 'Reopened' : 'Marked resolved');
      invalidate();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Update failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteReview(id),
    onSuccess: () => { toast.success('Deleted'); invalidate(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Delete failed'),
  });

  return (
    <div data-review-id={parent.id} className={cn(
      'rounded-lg border transition',
      active ? 'border-accent-400 bg-accent-600/10' :
      isResolved ? 'border-slate-800/60 bg-slate-900/20 opacity-75' :
      'border-slate-800 bg-slate-900/40 hover:border-slate-700',
    )}>
      {/* Parent comment */}
      <button
        data-testid="review-open" data-time={parent.timestamp_sec ?? undefined}
        onClick={() => {
          onActivate();
          if (isVideo && parent.timestamp_sec != null && onSeek) onSeek(parent.timestamp_sec);
        }}
        className="w-full text-left p-2.5"
      >
        <div className="flex items-center gap-2 text-[10px] text-muted mb-1 flex-wrap">
          <span className="font-medium text-muted-strong">{parent.author_name}</span>
          {parent.timestamp_sec != null && (
            <span className="font-mono text-accent-300">@ {fmtTime(parent.timestamp_sec)}</span>
          )}
          {hasDrawing && (
            <span className="text-amber-400 inline-flex items-center gap-0.5">
              <Star size={9} fill="currentColor" /> {parent.drawing!.length}
            </span>
          )}
          {isResolved && (
            <span className="text-emerald-400 inline-flex items-center gap-0.5">
              <CheckCircle2 size={10} /> resolved
            </span>
          )}
          <span className="ml-auto">{relTime(parent.created_at)}</span>
        </div>
        {parent.body !== '(voice note)' && <MentionBody text={parent.body} className="text-xs text-muted-strong" />}
        <VoiceClip url={parent.audio_url} />
      </button>

      {/* Replies */}
      {replies.length > 0 && (
        <div className="border-t border-slate-800/60 bg-slate-950/40 px-2.5 py-2 space-y-1.5">
          {replies.map(r => (
            <div key={r.id} className="text-[11px] text-muted-strong">
              <div className="flex items-center gap-1.5 text-[10px] text-muted">
                <span className="font-medium text-muted-strong">{r.author_name}</span>
                <span className="ml-auto">{relTime(r.created_at)}</span>
                {(isStaff || r.author_id === currentUserId) && (
                  <button
                    onClick={() => deleteMut.mutate(r.id)}
                    className="text-muted hover:text-red-400 -mr-0.5"
                    title="Delete reply"
                  >
                    <Trash2 size={10} />
                  </button>
                )}
              </div>
              {r.body !== '(voice note)' && <MentionBody text={r.body} className="pl-0.5" />}
              <VoiceClip url={r.audio_url} />
            </div>
          ))}
        </div>
      )}

      {/* Action bar - reply / resolve / delete */}
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-t border-slate-800/60 text-[10px]">
        <button
          onClick={() => setComposerOpen((v) => !v)}
          className="inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-white/10 text-muted hover:text-muted-strong"
          title="Reply"
        >
          <Reply size={11} /> Reply
          {replies.length > 0 && <span className="text-muted">({replies.length})</span>}
        </button>
        {canResolve && (
          <button
            onClick={() => toggleResolve.mutate()}
            disabled={toggleResolve.isPending}
            className={cn(
              'inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-white/10',
              isResolved ? 'text-amber-300 hover:text-amber-200' : 'text-emerald-400 hover:text-emerald-300',
            )}
            title={isResolved ? 'Reopen' : 'Resolve'}
          >
            {isResolved ? <><RotateCcw size={11} /> Reopen</> : <><CheckCircle2 size={11} /> Resolve</>}
          </button>
        )}
        {(isStaff || parent.author_id === currentUserId) && (
          <button
            onClick={() => deleteMut.mutate(parent.id)}
            className="inline-flex items-center gap-1 px-1.5 py-1 rounded text-muted hover:bg-white/10 hover:text-red-400 ml-auto"
            title="Delete thread"
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>

      {/* Inline reply composer (expanded on click) */}
      {composerOpen && (
        <div className="px-2.5 pb-2.5 pt-0">
          <MentionTextarea
            value={body}
            onChange={setBody}
            mentions={mentions}
            onMentionsChange={setMentions}
            placeholder="Write a reply... type @ to mention"
            rows={2}
            autoFocus
            className="text-xs px-2 py-1.5"
          />
          <div className="flex items-center justify-end gap-1.5 mt-1.5">
            <button
              onClick={() => { setComposerOpen(false); setBody(''); }}
              className="text-[10px] px-2 py-1 rounded text-muted hover:text-muted-strong"
            >
              Cancel
            </button>
            <button
              onClick={() => body.trim() && replyMut.mutate()}
              disabled={!body.trim() || replyMut.isPending}
              className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded bg-accent-500 hover:bg-accent-400 disabled:opacity-50 text-white font-medium"
            >
              <Send size={10} /> {replyMut.isPending ? 'Posting...' : 'Reply'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
